/**
 * Step-by-step help for getting reminders working on THIS device — what to
 * tap when the reminders switch can't simply be turned on yet. Picked by
 * `useReminderPrefs`'s `support` + `device`, rendered by
 * `components/ReminderGuidance.tsx` in both the More/Settings row and the
 * Today opt-in card.
 *
 * Copy rules: plain, short steps a farm hand can follow one-handed. Words in
 * quotes, and the Settings paths, are the phone's OWN labels ("Add to Home
 * Screen", "Notifications") — they must match what's on screen, so they
 * are the one place the word "notifications" appears. The user guide
 * (`docs-site/weather-notifications/`, `docs-site/offline-pwa/`) quotes these
 * strings — keep them in step.
 */

import type { DeviceInfo } from "./reminders";

export interface ReminderGuidance {
  intro: string;
  steps: string[];
}

const FINISH_STEP = "Come back here and turn on Daily reminders.";

function addToHomeScreenSteps(device: DeviceInfo): string[] {
  const share =
    device.browser === "chrome"
      ? "Tap the Share icon in the address bar."
      : "Tap the Share button (the square with an arrow pointing up). If you don't see it, tap ••• first.";
  const steps = [
    share,
    'Tap "Add to Home Screen", then "Add".',
    "Open Hayloft from the new icon on your Home Screen.",
    FINISH_STEP,
  ];
  if (device.browser !== "safari" && device.browser !== "chrome") {
    return ["Open this page in Safari.", ...steps];
  }
  return steps;
}

/** iPhone/iPad in a browser tab — reminders only work from the Home Screen. */
export function installGuidance(device: DeviceInfo): ReminderGuidance {
  return {
    intro: `To get reminders on this ${device.noun}, add Hayloft to your Home Screen first:`,
    steps: addToHomeScreenSteps(device),
  };
}

/** iPhone/iPad below 16.4 — no web push at all until it's updated. */
export function updateGuidance(device: DeviceInfo): ReminderGuidance {
  return {
    intro: `Reminders need a newer version of iOS on this ${device.noun} (16.4 or later).`,
    steps: [
      "Open Settings → General → Software Update and install the update.",
      "Then come back to Hayloft — we'll show you the last couple of steps.",
    ],
  };
}

/** The phone/browser itself has blocked reminders for Hayloft. */
export function unblockGuidance(device: DeviceInfo): ReminderGuidance {
  const intro = `Your ${device.noun} is blocking reminders for Hayloft. To unblock them:`;

  if (device.platform === "ios") {
    return {
      intro,
      steps: [
        `Open your ${device.noun}'s Settings app.`,
        "Tap Notifications, then Hayloft.",
        "Turn on Allow Notifications.",
        FINISH_STEP,
      ],
    };
  }

  if (device.platform === "android" && device.standalone) {
    return {
      intro,
      steps: [
        "Open your phone's Settings app.",
        "Tap Apps, then Hayloft, then Notifications.",
        "Turn notifications on.",
        FINISH_STEP,
      ],
    };
  }

  if (device.platform === "android") {
    return {
      intro,
      steps: [
        "Tap the lock or site-settings icon next to the web address.",
        "Tap Notifications and choose Allow. If you don't see it, tap Site settings first.",
        "Reload this page, then turn on Daily reminders.",
      ],
    };
  }

  if (device.browser === "safari") {
    return {
      intro,
      steps: [
        "In the menu bar, choose Safari → Settings → Websites.",
        "Click Notifications, find Hayloft, and choose Allow.",
        "Reload this page, then turn on Daily reminders.",
      ],
    };
  }

  return {
    intro,
    steps: [
      "Click the lock or site-settings icon at the left of the address bar.",
      "Set Notifications to Allow.",
      "Reload this page, then turn on Daily reminders.",
    ],
  };
}

/** Desktop/Android browser with no web push at all. */
export const UNSUPPORTED_HINT =
  "This browser can't show reminders. Try opening Hayloft in Chrome, Edge, Firefox or Safari.";
